import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../services/AxiosInstance';
import { sortSubscriptionPlansBySortOrder } from '../../../utils/subscriptionPlansSort';
import {
  getModuleTabsFromPlans,
  getModuleTabIcon,
  getModuleTabLabel,
  getModuleDescriptionForTab,
  planMatchesModuleTab,
  subscriptionPlansApiUrl,
  isSnapBillModuleTab,
} from '../../../utils/subscriptionModuleTabs';
import SnapBillPlanCreditNote from '../SnapBillPlanCreditNote/SnapBillPlanCreditNote';
import useMediaQuery from '../../../hooks/useMediaQuery';
import './PricingCards.css';

const MOBILE_MODULE_PICKER_QUERY = '(max-width: 991px)';

const PricingCards = () => {
  const isMobileModulePicker = useMediaQuery(MOBILE_MODULE_PICKER_QUERY);
  const [activeTab, setActiveTab] = useState('plans');
  const [userSubscription, setUserSubscription] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [activeModuleTab, setActiveModuleTab] = useState('task'); // 'task', 'document'
  const [modulesList, setModulesList] = useState([]);
  const navigate = useNavigate();
  const getPlans = async () => {
    const response = await axiosInstance.get(subscriptionPlansApiUrl());
    setSubscriptionPlans(sortSubscriptionPlansBySortOrder([...response.data.data]));
  };

  useEffect(() => {
    getPlans();
  }, []);

  useEffect(() => {
    if (subscriptionPlans.length === 0) return;
    const tabs = getModuleTabsFromPlans(subscriptionPlans);
    if (tabs.length > 0 && !tabs.some((t) => t.key === activeModuleTab)) {
      setActiveModuleTab(tabs[0].key);
    }
  }, [subscriptionPlans, activeModuleTab]);

  // Fetch modules list
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await axiosInstance.get('/modules/list');
        // Handle different response structures
        const modulesData = response.data || response;
        // Ensure it's an array
        if (Array.isArray(modulesData)) {
          setModulesList(modulesData);
        } else if (modulesData && Array.isArray(modulesData.data)) {
          setModulesList(modulesData.data);
        } else {
          setModulesList([]);
        }
      } catch (error) {
        console.error('Error fetching modules:', error);
        setModulesList([]);
      }
    };

    fetchModules();
  }, []);

  const getActivePlan = () => {
    if (!userSubscription || !subscriptionPlans.length) return null;
    return subscriptionPlans.find(plan => plan.PlanId === userSubscription.SubscriptionPlanId);
  };

  const activePlan = getActivePlan();
  const user = localStorage.getItem('user')

  const moduleTabs = getModuleTabsFromPlans(subscriptionPlans);
  const visibleModuleTabs = moduleTabs.length > 0
    ? moduleTabs
    : [
        { key: 'task', label: getModuleTabLabel('task') },
        { key: 'document', label: getModuleTabLabel('document') },
      ];

  const getPlansByModule = (moduleType) => {
    return subscriptionPlans.filter((plan) => planMatchesModuleTab(plan, moduleType));
  };

  const filteredPlans = getPlansByModule(activeModuleTab);

  const buildSubscriptionQuery = (plan) => {
    const planId = plan.id ?? plan.PlanId;
    const params = new URLSearchParams();
    if (planId != null) params.set('plan', String(planId));
    if (activeModuleTab) {
      params.set('module', activeModuleTab);
    }
    const q = params.toString();
    return q ? `?${q}` : '';
  };

  const selectPlanHandler = (plan) => {
    const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    const subscriptionPath = `/user-subscription${buildSubscriptionQuery(plan)}`;

    if (accessToken && user) {
      const userData = JSON.parse(user);
      const type = userData.type;
      if (type === 'corporate') {
        navigate(subscriptionPath);
      }
    } else {
      const planId = plan.id ?? plan.PlanId;
      navigate(`/register?plan=${planId}${activeModuleTab ? `&module=${activeModuleTab}` : ''}`);
      sessionStorage.setItem('redirectAfterLogin', subscriptionPath);
      sessionStorage.setItem('selectedPlan', planId);
    }
  };

  const renderPlansTab = () => (
    <div className="plans-container-home container">

      <p className="text-muted pricing-home-sub-title" style={{marginBottom: '0rem', paddingTop: '1rem'}}>
        Our plans are organized to
        suit your requirements. Start with our FREE plan option to test the
        platform. No credit card details or payment required to start the
        Free Plan. For payment based subscription, we will be collecting
        your credit card for monthly/yearly charges

      </p>

      {/* Module picker: dropdown on phones/tablets, tabs on desktop */}
      <div className="module-tabs-container mb-4">
        {isMobileModulePicker ? (
          <label className="module-tabs-select-wrap" htmlFor="pricing-module-select">
            <span className="module-tabs-select-label">Module</span>
            <select
              id="pricing-module-select"
              className="module-tabs-select"
              value={activeModuleTab}
              onChange={(event) => setActiveModuleTab(event.target.value)}
            >
              {visibleModuleTabs.map((tab) => (
                <option key={tab.key} value={tab.key}>
                  {tab.label}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div className="module-tabs">
            {visibleModuleTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`module-tab ${activeModuleTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveModuleTab(tab.key)}
              >
                <i className={`${getModuleTabIcon(tab.key)} me-2`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="module-description-wrapper">
        <p style={{ fontSize: '1.2rem', lineHeight: '1.6', textAlign: 'left', marginBottom: '0rem', color: '#89879f' }}>
          {getModuleDescriptionForTab(activeModuleTab, { modulesList, subscriptionPlans })}
        </p>
      </div>

      {/* Billing Cycle Toggle */}
      <div className="billing-toggle-container mb-2">
        <div className="billing-toggle">
          <span className={`toggle-label ${billingCycle === 'monthly' ? 'active' : ''}`}>
            Monthly
          </span>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={billingCycle === 'yearly'}
              onChange={(e) => setBillingCycle(e.target.checked ? 'yearly' : 'monthly')}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className={`toggle-label ${billingCycle === 'yearly' ? 'active' : ''}`}>
            Yearly
          </span>
        </div>
      </div>
      {filteredPlans.length === 0 ? (
        <div className="text-center py-5">
          <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
          <p className="text-muted">No plans available for this module.</p>
        </div>
      ) : (
        <div className="plans-grid">
          {filteredPlans.map((plan) => {
            const isActive = activePlan && activePlan.PlanId === plan.PlanId;
            const isPopular = plan.PlanId === 2;
            const rawPrice = billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price;
            const priceNum = parseFloat(rawPrice) || 0;

            return (
              <div
                key={plan.PlanId ?? plan.id}
                className={`plan-card ${isActive ? 'active-plan' : ''}`}
              >
                {isActive && (
                  <div className="active-badge">
                    <i className="fas fa-check-circle me-1"></i>
                    Current Plan
                  </div>
                )}
                {isPopular && !isActive && (
                  <div className="popular-plan-badge">
                    <span className="popular-plan-badge-inner">
                      <i className="fas fa-fire me-1"></i>
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="plan-header">
                  <div className="plan-title">{plan.display_name}</div>
                </div>

                <div className="plan-content">
                  <div className="plan-pricing-section text-center">
                    <div className="plan-pricing">
                      <span className="price-amount">
                        {`$${priceNum.toFixed(2)}`}
                      </span>
                      <span className="price-period">
                        /{billingCycle === 'yearly' ? 'year' : 'month'}
                      </span>
                    </div>
                    {billingCycle === 'yearly' && plan.monthly_price && (
                      <small className="plan-pricing-note text-muted d-block mt-1">
                        {`$${parseFloat(plan.monthly_price).toFixed(2)}`}/month billed annually
                      </small>
                    )}
                    {(plan.is_unlimited_users ||
                      (plan.billing_type === 'tiered' && plan.max_users)) && (
                      <div className="user-limit-info">
                        {plan.is_unlimited_users ? (
                          <p>Unlimited users</p>
                        ) : (
                          <p>{plan.max_users} users</p>
                        )}
                      </div>
                    )}
                  </div>

                  {plan.description && (
                    <div className="plan-description-section text-center">
                      <p className="plan-description">{plan.description}</p>
                    </div>
                  )}

                  {(() => {
                    const safeParse = (value) => {
                      if (!value) return [];
                      if (Array.isArray(value)) return value;
                      if (typeof value === 'string') {
                        if (value.trim().startsWith('[') || value.trim().startsWith('{')) {
                          try {
                            return JSON.parse(value);
                          } catch (e) {
                            return [value];
                          }
                        }
                        return value.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
                      }
                      return [];
                    };

                    const included =
                      plan.whats_included && plan.whats_included.length > 0
                        ? safeParse(plan.whats_included)
                        : plan.features_list && plan.features_list.length > 0
                          ? safeParse(plan.features_list)
                          : [];

                    return (
                      included.length > 0 && (
                        <div className="plan-features">
                          <h6 className="features-title text-center">What&apos;s Included:</h6>
                          <ul className="features-list">
                            {included.map((item, idx) => {
                              const text =
                                typeof item === 'object'
                                  ? item.display_name || item.name || JSON.stringify(item)
                                  : item;
                              return (
                                <li key={idx} className="feature-item">
                                  <i className="fas fa-check text-danger me-2"></i>
                                  <span className="feature-item-text" style={{ whiteSpace: 'pre-line' }}>
                                    {text}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )
                    );
                  })()}
                </div>

                <div className="plan-action">
                  <button
                    type="button"
                    className="select-plan-btn"
                    onClick={() => selectPlanHandler(plan)}
                  >
                    <i className="fas fa-arrow-right me-2"></i>
                    Select Plan
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {isSnapBillModuleTab(activeModuleTab) && <SnapBillPlanCreditNote />}
      <p>
        before continuing, ensure to read our <Link
          to="/terms-and-conditions"
          rel="noopener noreferrer"
          target="_blank"
          className="terms-link text-primary rounded"
        >
          terms and conditions
        </Link>
      </p>
    </div>
  );

  const renderManageTab = () => (
    <div className="manage-container">
      <div className="text-center mb-5">
        <h2 className="fw-bold text-dark mb-3">
          <i className="fas fa-cog text-primary me-2"></i>
          Manage Your Subscription
        </h2>
        <p className="text-muted fs-5">
          Update your subscription settings and payment information
        </p>
      </div>
    </div>
  );

  return (
    <div className={`${activeTab === 'plans' ? 'home-plans-container' : 'card-body p-4'} `}>
      {/* Modern Tab Navigation */}
      {/* Tab Content */}
      {activeTab === 'plans' ? renderPlansTab() : renderManageTab()}
      <style jsx>{`
        .subscription-page {
          min-height: 100vh;
        }
        .full-width-container {
          width: 100%;
          padding: 0;
          margin: 0;
        }
        .main-card {
          background: white;
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        }
        .manage-container {
          width: 100%;
          max-width: none;
        }
        .tab-navigation .nav-link {
          transition: all 0.3s ease;
        }
        
        .tab-navigation .nav-link:hover {
          transform: translateY(-2px);
        }

        .tab-navigation {
          display: flex;
          justify-content: center;
        }

        .tab-navigation .nav {
          width: auto;
          gap: 1rem;
        }

        .module-tabs-container {
          display: flex;
          justify-content: flex-start;
          margin-bottom: 1rem;
        }

        .module-tabs {
          display: flex;
          gap: 0;
          background: transparent;
          padding: 0;
          border-radius: 0;
          box-shadow: none;
          border: none;
          border-bottom: 2px solid #e9ecef;
        }

        .module-tab {
          padding: 0.75rem 2rem;
          border: none;
          background: transparent;
          border-radius: 0;
          font-weight: 600;
          font-size: 1rem;
          color: #6c757d;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          border-bottom: 3px solid transparent;
          margin-bottom: -2px;
          position: relative;
          flex: 1;
          max-width: 280px;
          white-space: nowrap;
        }

        .module-tab:hover {
          color: #C42126;
          background: transparent;
          border-bottom-color: #C42126;
        }

        .module-tab.active {
          background: transparent;
          color: #C42126;
          box-shadow: none;
          border-bottom-color: #C42126;
        }

        .module-tab i {
          font-size: 0.9rem;
        }

        .module-description-container {
          max-width: 900px;
          margin: 0 auto 1.5rem;
        }

        .module-description {
          display: flex;
          align-items: flex-start;
          gap: 1.5rem;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          padding: 1.5rem 2rem;
          border-radius: 15px;
          border-left: 4px solid #C42126;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
        }

        .module-description-icon {
          flex-shrink: 0;
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #C42126;
          color: white;
          border-radius: 12px;
          font-size: 1.5rem;
        }

        .module-description-content {
          flex: 1;
        }

        .module-description-title {
          color: #C42126;
          font-weight: 700;
          font-size: 1.25rem;
          margin-bottom: 0.75rem;
        }

        .module-description-text {
          color: #495057;
          font-size: 1rem;
          line-height: 1.7;
          margin: 0;
        }

        @media (max-width: 768px) {
          .tab-navigation .nav {
            flex-direction: column;
            width: 100%;
          }

          .module-tabs-container {
            width: 100%;
            justify-content: stretch;
          }

          .module-description {
            flex-direction: column;
            padding: 1.25rem 1.5rem;
            gap: 1rem;
          }

          .module-description-icon {
            width: 45px;
            height: 45px;
            font-size: 1.25rem;
          }

          .module-description-title {
            font-size: 1.1rem;
          }

          .module-description-text {
            font-size: 0.95rem;
          }
        }
      `}</style>
    </div>
  );
};

export default PricingCards; 